import { getUser, login, register, updateToken } from "../../api";
import axios from "axios";
import storage from "../storage";
import qs from "query-string";
import currencies from "../../constants/currencies";
import QUERY_PARAMS from "../../constants/queryParams";

const INITIALIZED = "INITIALIZED";
const LOGIN = "LOGIN";
const LOGIN_FAILED = "LOGIN_FAILED";
const LOGOUT = "LOGOUT";
const USER_SUCCESS = "USER_SUCCESS";
const USER_FAILED = "USER_FAILED";
const REGISTER_FAILED = "REGISTER_FAILED";
const LANGUAGE_SELECTED = "LANGUAGE_SELECTED";
const THEME_SELECTED = "THEME_SELECTED";
const CURRENCY_SELECTED = "CURRENCY_SELECTED";

const getSavedTheme = () =>
  process.env.PRESENTATION
    ? localStorage.getItem("bt_theme_presentation") || "betby-demo-blue-dark"
    : qs.parse(window.location.search)[QUERY_PARAMS.THEME_NAME] ||
      localStorage.getItem("bt_theme") ||
      "default";

const getDefaultCurrency = () => {
  const defaultCurrency = storage.getItem("bt_currency") || "EUR";
  const preferredCurrency =
    qs.parse(window.location.search)["currency"] || defaultCurrency;
  const formattedCurrency = preferredCurrency.toUpperCase();

  if (
    currencies.find(({ label }) => label.toUpperCase() === formattedCurrency)
  ) {
    return formattedCurrency;
  }
  return defaultCurrency;
};

const state = {
  error: false,
  isLoggedIn: false,
  token: null,
  username: null,
  currency: getDefaultCurrency(),
  oddsFormat: "DECIMAL",
  userId: null,
  lang: storage.getItem("bt_lang") || "en",
  initialized: false,
  theme: getSavedTheme(),
};

const mutations = {
  [REGISTER_FAILED](state, error) {
    state.error = error;
  },
  [LOGIN](state, token) {
    state.error = false;
    state.token = token;
    state.isLoggedIn = true;
  },
  [LOGIN_FAILED](state, error) {
    state.error = error;
    state.isLoggedIn = false;
    state.token = null;
  },
  [LOGOUT](state) {
    state.error = false;
    state.isLoggedIn = false;
    state.token = null;
  },
  [USER_SUCCESS](state, { username, lang, userId }) {
    state.username = username;
    state.userId = userId;
    state.lang = lang.toLowerCase();
    state.initialized = true;
  },
  [USER_FAILED](state, error) {
    state.error = error;
  },
  [INITIALIZED](state) {
    state.initialized = true;
  },
  [LANGUAGE_SELECTED](state, { lang }) {
    state.lang = lang;
  },
  [THEME_SELECTED](state, { theme }) {
    state.theme = theme;
  },
  [CURRENCY_SELECTED](state, currency) {
    state.currency = currency;
  },
};

const updateTokenInStorage = (token) => {
  if (token) {
    storage.setItem("token", token);
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    storage.removeItem("token");
    delete axios.defaults.headers.common.Authorization;
  }
};

const actions = {
  login({ dispatch, commit, rootState }, { email, password }) {
    const brandId = rootState.app.activeBrand;
    const currency = rootState.auth.currency;

    return login({ email, password, brandId, currency })
      .then((res) => {
        const { token } = res.data;

        if (!token) {
          return Promise.reject();
        }

        return Promise.resolve(token);
      })
      .then((token) => dispatch("getUser", token))
      .catch((error) => commit(LOGIN_FAILED, error));
  },
  updateToken({ commit }, data) {
    updateToken(data).then((res) => {
      const { token } = res.data;

      updateTokenInStorage(token);
      commit(LOGIN, token);
    });
  },
  logout({ commit }) {
    updateTokenInStorage();
    commit(LOGOUT);
  },
  register({ dispatch, commit, rootState }, user) {
    const brandId = rootState.app.activeBrand;

    return register({ ...user, brandId })
      .then(() => dispatch("login", user))
      .catch((error) => commit(REGISTER_FAILED, error));
  },
  getUser({ commit, dispatch, state }, token) {
    const userSelectedLang = storage.getItem("bt_lang");

    if (token) {
      return getUser(token)
        .then(({ data: { username, odds_format, lang, expire_at, id } }) => {
          updateTokenInStorage(token);
          commit(LOGIN, token);
          commit(USER_SUCCESS, {
            username,
            oddsFormat: odds_format,
            lang: userSelectedLang || lang,
            expireAt: expire_at,
            userId: id,
          });
        })
        .catch(() => {
          dispatch("logout");
        })
        .finally(() => commit(INITIALIZED));
    }

    return commit(INITIALIZED);
  },
  selectLanguage({ state, commit, dispatch }, { lang }) {
    commit(LANGUAGE_SELECTED, { lang });
    storage.setItem("bt_lang", lang);

    if (state.isLoggedIn) {
      dispatch("updateToken", { lang });
    }
  },
  selectCurrency({ state, commit, dispatch }, currency) {
    commit(CURRENCY_SELECTED, currency);
    storage.setItem("bt_currency", currency);

    if (state.isLoggedIn) {
      dispatch("updateToken", { currency });
    }
  },
  selectTheme({ commit }, { theme }) {
    process.env.PRESENTATION
      ? localStorage.setItem("bt_theme_presentation", theme)
      : localStorage.setItem("bt_theme", theme);
    commit(THEME_SELECTED, { theme });
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
